/**
 * A faixa de ação do bloco em edição (E202.F2, GUS-145, ADR-0145 §5–§7): o que fazer com o trecho
 * (reescrever, tirar, voltar ao original, conforme cabe), o fundamento obrigatório, o estado do
 * rascunho, a falha da gravação (o texto fica no bloco), "Salvar" com carregamento e "Descartar".
 */
import { Button, Field, Seg, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  ACOES_COM_TEXTO,
  ACOES_DO_TIPO,
  FUNDAMENTO_MINIMO,
  type MotivoDaFalha,
  type TrechoNaTelaData,
} from '@/lib/correcaoDoRascunho';
import {
  cabe,
  faltaParaSalvar,
  FUNDAMENTO_MAXIMO,
  rotuloDaAcao,
  TEXTO_MAXIMO,
  type ConteudoDoEditor,
  type SituacaoDoRascunho,
} from '@/lib/editorManual';

import { EstadoDoRascunho, type EstadoDaGravacao } from './rascunhoDoBloco';

const t = ptBR.documentoDaMesa.edicao;
const tm = ptBR.editorManual;
const tc = ptBR.correcaoDoRascunho;

export interface FaixaDoBlocoProps {
  trecho: TrechoNaTelaData;
  conteudo: ConteudoDoEditor;
  onMudar: (conteudo: ConteudoDoEditor) => void;
  /** O texto que sai hoje no documento, em markup: reescrever exige mudar o texto. */
  noDocumento: string;
  estado: EstadoDaGravacao;
  situacao: SituacaoDoRascunho | null;
  salvando: boolean;
  falha: MotivoDaFalha | null;
  onSalvar: () => void;
  onDescartar: () => void;
  onFechar: () => void;
}

/** O que falta para salvar; `null` quando dá. */
function faltaNaFaixa(conteudo: ConteudoDoEditor, noDocumento: string): string | null {
  const falta = faltaParaSalvar(conteudo);
  if (falta !== null) {
    return tm.editor.falta[falta]
      .replace('{minimo}', String(FUNDAMENTO_MINIMO))
      .replace('{maximo}', String(TEXTO_MAXIMO));
  }
  if (ACOES_COM_TEXTO.has(conteudo.acao) && conteudo.texto.trim() === noDocumento.trim()) {
    return t.semMudanca;
  }
  return null;
}

function Acoes({
  trecho,
  conteudo,
  onMudar,
}: Pick<FaixaDoBlocoProps, 'trecho' | 'conteudo' | 'onMudar'>) {
  const acoes = ACOES_DO_TIPO[trecho.tipo].filter((acao) => cabe(trecho, acao));
  if (acoes.length < 2) return null;
  return (
    <Seg
      label={t.acaoRotulo}
      value={conteudo.acao}
      onChange={(acao) => onMudar({ ...conteudo, acao })}
      options={acoes.map((acao) => ({
        value: acao,
        label: rotuloDaAcao(trecho, acao),
        testId: `acao-${acao}`,
      }))}
    />
  );
}

export function FaixaDoBloco(props: FaixaDoBlocoProps): JSX.Element {
  const { conteudo, onMudar, salvando, falha } = props;
  const falta = faltaNaFaixa(conteudo, props.noDocumento);
  return (
    <div
      className="mt-2 space-y-sc-2 border-t border-border bg-surface-2 p-sc-3 font-sans text-body-sm text-fg"
      data-testid="faixa-do-bloco"
    >
      <Acoes trecho={props.trecho} conteudo={conteudo} onMudar={onMudar} />
      {conteudo.acao === 'remover' ? <p data-testid="aviso-da-acao">{t.sai}</p> : null}
      {conteudo.acao === 'restaurar' ? <p data-testid="aviso-da-acao">{t.volta}</p> : null}
      <Field
        label={t.fundamento}
        description={t.fundamentoAjuda.replace('{minimo}', String(FUNDAMENTO_MINIMO))}
        required
      >
        <Textarea
          rows={2}
          value={conteudo.fundamento}
          maxLength={FUNDAMENTO_MAXIMO}
          onChange={(e) => onMudar({ ...conteudo, fundamento: e.target.value })}
          data-testid="fundamento-do-bloco"
        />
      </Field>
      <EstadoDoRascunho estado={props.estado} situacao={props.situacao} />
      {falha !== null ? (
        <p role="alert" className="text-danger-700" data-testid="falha-do-bloco">
          {t.naoSalva} {tc.erro[falha]}
        </p>
      ) : null}
      {falta !== null ? (
        <p className="text-caption text-fg-muted" data-testid="falta-para-salvar">
          {falta}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-sc-2">
        <Button
          type="button"
          size="sm"
          loading={salvando}
          disabled={falta !== null}
          onClick={props.onSalvar}
          data-testid="salvar-bloco"
        >
          {salvando ? t.salvando : t.salvar}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={salvando}
          onClick={props.onDescartar}
          data-testid="descartar-bloco"
        >
          {t.descartar}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={props.onFechar}
          disabled={props.salvando}
          title={t.fecharAjuda}
          data-testid="fechar-bloco"
        >
          {t.fechar}
        </Button>
      </div>
    </div>
  );
}
